/* Shuffle / scramble text on buttons (OUTLYR SAP Rebuild, 2026-09-16).
 *
 * On hover or focus, a button's label resolves character by character:
 * each position cycles through random glyphs for a moment, then locks to
 * its real letter, left to right. Departure Mono is a MONOSPACE, which is
 * what makes this work -- every substitute glyph occupies the same width
 * as the letter it stands in for, so the label never reflows and nothing
 * beside it shifts. Do not apply this to a proportional face.
 *
 * Scope (Josh's pick, 2026-09-16): real buttons and CTAs only. Nav links,
 * field labels, prices and badges also use Departure Mono, but scrambling
 * a price or a form label reads as a glitch rather than a flourish.
 *
 * Accessibility:
 *   - The element's accessible name is pinned with aria-label BEFORE any
 *     scrambling starts, so a screen reader always announces the real
 *     label and never the random intermediate glyphs.
 *   - prefers-reduced-motion disables the effect entirely; the label just
 *     sits there as normal text.
 *   - Only the text node is touched. Buttons carrying an icon or a nested
 *     <span> (the hero CTA's arrow, the footer's visually-hidden label)
 *     are skipped rather than having their markup flattened.
 */
(() => {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* Glyph pool. Uppercase plus digits and a few technical marks -- the
     labels are all uppercase, so lowercase substitutes would visibly
     change the text's weight and rhythm mid-shuffle. */
  const GLYPHS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789/\\|<>*+=#%';

  /* SCOPE (widened 2026-09-17 after an audit: only 6 of 16 mono controls
     on the homepage were bound, 2 of 13 on the product page).

     Josh asked for the menu bar specifically ("ie menu bar"), which is
     the header nav -- those anchors render with NO class of their own, so
     they are reached via .header__menu a rather than a class.

     Still deliberately EXCLUDED, per Josh's original scoping call: mono
     elements that are not controls -- .product-hero__label,
     .product-hero__trustbadge, .story__marker-label, .policies__link-blurb,
     prices and field labels. Scrambling a price or a form label reads as a
     glitch rather than a flourish. */
  const SELECTOR = [
    /* header / menu bar */
    '.header__menu a',
    '.header__dropdown-trigger',
    '.header__dropdown a',
    '.header__drawer-menu a',
    '.header__cart',
    '.header__menu-toggle',

    /* buttons + CTAs */
    '.product-hero__submit',
    '.product-hero__pill',
    '.contact__submit',
    '.newsletter__submit',
    '.search-page__submit',
    '.footer__submit',
    '.cta-band__button',
    '.hero-product__cta',
    '.hero-video__cta',
    '.bundle-promo__cta',
    '.product-stage__cta',
    '.product-cards__card-cta',
    '.collection-tiles__card-cta',
    '.story__cta',
    '.story-video__cta',
    '.stockist__cta',
    '.cart-button',
    /* Cart drawer links -- found by a census that showed 18 of 20 mono
       controls bound; these two were the remainder. Plain text links,
       so nothing special is needed beyond listing them. */
    '.cart-line__remove',
    '.cart-drawer__viewcart',
    '.policies__button',
  ].join(',');

  /* One shuffle run per element.
   *
   * Timing follows the reference component Josh supplied (21st.dev
   * shuffle-button): a DURATION rather than a fixed per-character step,
   * so a long label and a short one both resolve in about the same time
   * instead of the long one crawling. Characters lock left-to-right on
   * enter and right-to-left on leave, and glyphs cycle on a 25ms beat.
   *
   * Ported rather than installed: that component is React + Tailwind +
   * shadcn's cn(), and this is a Liquid theme with no React and no build
   * step. The behaviour is what matters and it carries over exactly.
   *
   * `direction` is 1 for enter (resolve from the left) and -1 for leave
   * (resolve from the right), which is what gives the exit its own
   * motion instead of snapping back to the label.
   *
   * Returns a cancel function so a re-enter mid-run restarts cleanly
   * rather than interleaving two animations over one element.
   */
  const shuffle = (node, text, direction, duration) => {
    const chars = [...text];
    const real = chars.filter((c) => c !== ' ').length || 1;
    /* Per-character step, derived from the total duration as the
       reference does. Clamped so a very long label cannot make the step
       so small that nothing is legible. */
    const step = Math.max(18, (duration * 500) / real);

    const order = chars.map((_, i) => (direction === 1 ? i : chars.length - 1 - i));

    const startedAt = performance.now();
    let lastRoll = 0;
    let rolled = chars.slice();
    let raf = null;

    const tick = (now) => {
      const elapsed = now - startedAt;

      /* Glyphs churn on their own 25ms beat, independent of the lock
         schedule -- matching the reference's setInterval(25). */
      if (now - lastRoll >= 25) {
        lastRoll = now;
        rolled = chars.map((ch) => (ch === ' ' ? ' ' : GLYPHS[(Math.random() * GLYPHS.length) | 0]));
      }

      node.nodeValue = order
        .map((pos, rank) => {
          const ch = chars[pos];
          if (ch === ' ') return { pos, out: ' ' };
          /* This position is settled once its turn has elapsed. */
          const settled = elapsed >= step * (rank + 1);
          return { pos, out: settled ? ch : rolled[pos] };
        })
        .sort((a, b) => a.pos - b.pos)
        .map((x) => x.out)
        .join('');

      if (elapsed < step * chars.length) {
        raf = requestAnimationFrame(tick);
      } else {
        node.nodeValue = text;
        raf = null;
      }
    };

    raf = requestAnimationFrame(tick);

    return () => {
      if (raf) cancelAnimationFrame(raf);
      node.nodeValue = text;
    };
  };

  /* The element's own label, as a TEXT NODE.
   *
   * This replaces a blanket `if (el.children.length > 0) return;`, which
   * skipped four of the buttons Josh noticed were dead: .header__cart
   * (wraps <span data-cart-count>), .hero-product__cta (arrow span + svg),
   * .footer__submit (visually-hidden label + svg) and .header__menu-toggle
   * (svg + label span).
   *
   * Writing el.textContent on any of those would delete the icon or the
   * live cart count. So instead we find the biggest text node and animate
   * only that -- siblings are never touched. For .header__cart that is
   * "Cart (" , which is exactly right: the count keeps updating itself.
   */
  const labelNode = (el) => {
    let best = null;
    for (const node of el.childNodes) {
      if (node.nodeType !== Node.TEXT_NODE) continue;
      const len = node.nodeValue.trim().length;
      if (len && (!best || len > best.nodeValue.trim().length)) best = node;
    }
    if (best) return best;

    /* FALLBACK: the label lives in a child SPAN, not a direct text node.
       Measured -- .hero-product__cta and .header__menu-toggle have only
       whitespace text nodes ("\n          ") with the real word inside a
       span, so the loop above found nothing and they were silently
       skipped. That is why Josh saw them not animating.

       Takes the first child element carrying a single text node, which is
       the label span in every case here. The icon svg alongside it has no
       text, so it is never picked.

       .footer__submit is deliberately left OUT of this: its only label is
       a .visually-hidden span, and scrambling text nobody can see buys
       nothing while still churning the DOM. */
    for (const child of el.children) {
      if (child.classList.contains('visually-hidden')) continue;
      const first = child.firstChild;
      if (
        child.childNodes.length === 1 &&
        first &&
        first.nodeType === Node.TEXT_NODE &&
        first.nodeValue.trim().length
      ) {
        return first;
      }
    }
    return null;
  };

  document.querySelectorAll(SELECTOR).forEach((el) => {
    const node = labelNode(el);
    if (!node) return;

    const text = node.nodeValue.trim();
    if (!text) return;

    /* Pin the accessible name before the text ever changes, so a screen
       reader never announces the scrambled intermediate glyphs. Uses the
       element's FULL text, not just this node, so "Cart (0)" stays whole. */
    if (!el.hasAttribute('aria-label')) {
      el.setAttribute('aria-label', el.textContent.replace(/\s+/g, ' ').trim());
    }

    node.nodeValue = text;

    let cancel = null;

    /* duration in seconds, matching the reference's default of 1. */
    const DURATION = 1;

    const run = (direction) => {
      if (reduced.matches) return;
      if (cancel) cancel();
      cancel = shuffle(node, text, direction, DURATION);
    };

    /* Enter resolves left-to-right; leave resolves right-to-left, so the
       exit has its own motion rather than cutting back to the label. */
    el.addEventListener('mouseenter', () => run(1));
    el.addEventListener('focus', () => run(1));
    el.addEventListener('mouseleave', () => run(-1));
    el.addEventListener('blur', () => run(-1));
  });
})();
